<template>
    <div>
        <div v-if="value.length" class="mb-36">
            <small class="u-bold">Ausgewählte Dateien:</small>
            <div v-for="(file, i) in value" :key="`file-${i}`">
                <small>{{ file.name }}</small>
                <button class="u-reset-button text-error" title="Datei entfernen" @click="removeFile(i)">
                    <icon name="x" hidden />
                </button>
            </div>
        </div>

        <div
            v-else
            ref="dropFile"
            class="drop-file u-flex u-flex--center p-24 u-text-center"
            @drop="handleFileDrop"
            @click="triggerClick"
        >
            <span class="text-mute">
                <template v-if="dragAndDropEnabled">Dateien hier ablegen oder klicken</template>
                <template v-else>Drag and Drop wird nicht unterstützt</template>
            </span>
            <input
                ref="file"
                type="file"
                class="u-display-none"
                :accept="allowedTypes.join(',')"
                multiple
                @change="onBrowseFile"
            />
        </div>

        <div v-if="errors.length" class="text-error">
            <p v-for="(error, i) in errors" :key="`error-${i}`">{{ error }}</p>
        </div>
    </div>
</template>

<script>
import { dissoc } from 'ramda'
import Icon from '@/components/ui/Icon.vue'

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg']

export default {
    name: 'DropFile',

    components: { Icon },

    props: {
        value: { type: Array, default: () => [] },
        allowedTypes: { type: Array, default: () => ALLOWED_TYPES },
        maxFiles: { type: Number, default: 18 },
    },

    data: () => ({
        dragAndDropEnabled: false,
        errors: [],
        browsedFile: null,
    }),

    watch: {
        async value (value) {
            if (!value?.length && this.dragAndDropEnabled) {
                await this.$nextTick()
                this.bindEvents()
            }
        },
    },

    mounted () {
        // If drag and drop is capable, then we continue to bind events to our elements.
        this.dragAndDropEnabled = this.isDragAndDropCapable()
        if (this.dragAndDropEnabled) {
            this.bindEvents()
        }
    },

    methods: {
        removeFile (index) {
            this.$emit('input', dissoc(index, this.value))
        },

        bindEvents () {
            // Listen to all drag events and bind an event listener to each for the drop zone.
            ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(function (event) {
                /*
                    For each event add an event listener that prevents the default action
                    (opening the file in the browser) and stop the propagation of the event (so
                    no other elements open the file in the browser)
                */
                this.$refs?.dropFile.addEventListener(event, function (e) {
                    e.preventDefault()
                    e.stopPropagation()
                }.bind(this), false)
            }.bind(this))
        },

        triggerClick () {
            this.$refs.file.click()
        },

        isDragAndDropCapable () {
            /**
             * Create a test element to see if certain events
             * are present that let us do drag and drop.
             */
            const div = document.createElement('div')

            /**
             * Check to see if the `draggable` event is in the element
             * or the `ondragstart` and `ondrop` events are in the element.
             * If they are, then we have what we need for dragging and dropping files.
             *
             * We also check to see if the window has `FormData` and `FileReader` objects present,
             * so we can do our AJAX uploading
             */
            return (('draggable' in div) || ('ondragstart' in div && 'ondrop' in div))
                && 'FormData' in window
                && 'FileReader' in window
        },

        handleFileDrop (event) {
            this.errors = [] // Reset
            const files = Array.from(event.dataTransfer.files)

            if (files.length > this.maxFiles) {
                this.errors.push(`Du darfst maximal ${this.maxFiles} Dateien hochladen.`)
            }

            files.forEach(file => {
                if (!this.allowedTypes.includes(file?.type)) {
                    this.errors.push(`Der Dateityp ${file.type} der Datei ${file.name} ist nicht erlaubt.`)
                }
            })

            if (this.errors?.length) return

            this.$emit('input', files)
        },

        onBrowseFile (event) {
            this.errors = [] // Reset
            const files = Array.from(event?.target?.files)

            if (files.length > this.maxFiles) {
                this.errors.push(`Du darfst maximal ${this.maxFiles} Dateien hochladen.`)
            }

            files.forEach(file => {
                if (!this.allowedTypes.includes(file?.type)) {
                    this.errors.push(`Der Dateityp ${file.type} der Datei ${file.name} ist nicht erlaubt.`)
                }
            })

            if (this.errors?.length) return

            this.$emit('input', files)
        },
    },
}
</script>

<style lang="scss" scoped>
.drop-file {
    height: 50vh;
    width: 100%;
    background: darken($tertiary-color, 10%);
    cursor: pointer;
}
</style>
